import { useMemo } from "react";
import ChevronLeftIcon from "@heroicons/react/20/solid/ChevronLeftIcon";
import { Link, Navigate, useSearchParams } from "react-router-dom";

import useFCSubscriptionStore from "@hsl/fc-subscription/store";

import { type Config } from "../../../config/types";
import { Translate } from "../../../hooks/useTranslation";
import Loading from "../../Loading";
import Tabs from "../../Tabs";
import { AcceptFunds } from "../AcceptFunds";
import { useFundCentres } from "../useFundCentres";
import { useHideTabBar } from "../useHideTabBar";
import { EditYourUserDetails } from "./Tabs/EditDetails/EditDetails";
import { MySubscriptionsAndWatchLists } from "./Tabs/MySubscriptions/MySubscriptions";
import { WeeklyRoundupEmailTimings } from "./Tabs/RoundupTimings/RoundupTimings";

interface Props {
    collectivesConfig: Config;
    etfConfig: Config;
    pmcConfig: Config;
}

export const ManageMyFunds = ({
    collectivesConfig,
    etfConfig,
    pmcConfig,
}: Props) => {
    useHideTabBar();
    const params = useSearchParamString();
    const user = useFCSubscriptionStore((state) => state.user);
    const loadingUser = useFCSubscriptionStore((state) => state.loadingUser);
    const latestFC = useFCSubscriptionStore((state) => state.latestFC);
    const subscriptions = useFCSubscriptionStore(
        (state) => state.userSubscriptions,
    );

    const subscribedFunds = Array.from(
        new Set(subscriptions.map((s) => s.fundId)),
    );

    const fundCentres = useFundCentres({
        collectivesConfig,
        etfConfig,
        pmcConfig,
        subscribedFunds,
    });

    const tabs = useMemo(() => {
        return [
            {
                key: "my-subscriptions",
                label: (
                    <>
                        <span className="sm:hidden">
                            <Translate>Manage</Translate>
                        </span>
                        <span className="hidden sm:inline-block">
                            <Translate>
                                My subscriptions and watchlist
                            </Translate>
                        </span>
                    </>
                ),
            },
            {
                key: "weekly-roundup",
                label: (
                    <>
                        <span className="sm:hidden">
                            <Translate>Email timings</Translate>
                        </span>
                        <span className="hidden sm:inline-block">
                            <Translate>Weekly round-up email timings</Translate>
                        </span>
                    </>
                ),
            },
            {
                key: "edit-details",
                label: (
                    <>
                        <span className="sm:hidden">
                            <Translate>User details</Translate>
                        </span>
                        <span className="hidden sm:inline-block">
                            <Translate>Edit your user details</Translate>
                        </span>
                    </>
                ),
            },
        ];
    }, []);

    const content = useMemo(() => {
        if (!user) {
            return [1, 2, 3].map((i) => <Loading key={i} />);
        }
        return [
            <MySubscriptionsAndWatchLists
                key="my-subscriptions"
                fundCentres={fundCentres}
            />,
            <WeeklyRoundupEmailTimings key="weekly-roundup" user={user} />,
            <EditYourUserDetails key="edit-details" user={user} />,
        ];
    }, [fundCentres, user]);

    if (loadingUser) {
        return <Loading />;
    }

    if (!user) {
        return <Navigate to={latestFC + params} />;
    }

    return (
        <main className="w-full px-4 pb-16 pt-px sm:px-0">
            <Link
                to={`/my-funds${params}`}
                className="text-taupe-grey my-3 inline-flex flex-row items-center py-2 pr-2"
            >
                <ChevronLeftIcon className="mr-1.5 inline-block h-5 w-5" />
                <Translate>Back to my funds</Translate>
            </Link>
            <h1 className="text-lg font-medium sm:text-3xl sm:font-normal">
                <Translate>Manage subscriptions and watchlist</Translate>
            </h1>
            <AcceptFunds fundCentres={fundCentres} className="my-8" />
            <Tabs className="mt-4" tabs={tabs} content={content} withBorder />
        </main>
    );
};

export const useSearchParamString = () => {
    const [searchParams] = useSearchParams();
    const string = searchParams.toString();
    if (string) return `?${string}`;
    return "";
};
